import { useState } from "react";
import api from "@/lib/api";
import { formatApiError, limitWords, countWords } from "@/lib/format";
import { toast } from "sonner";
import { Gate, ready, Panel, useLoad, stamp, Chip, Table, Filters, Modal, fieldStyle, Label } from "@/components/console/ui";
import { useProfileViewer } from "@/components/Layout";

export function Users() {
  const [q, setQ] = useState("");
  const [rows] = useLoad(`/console/users?q=${encodeURIComponent(q)}`, 20000);
  const { viewProfile } = useProfileViewer();
  return (
    <Panel title="user directory" testId="console-users" right={<span className="text-xs">{ready(rows) ? rows.length : "…"} accounts</span>}>
      <input data-testid="users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="search username…" className="w-full md:w-80 px-3 py-2 text-sm mb-4" style={fieldStyle} />
      <p className="text-[10px] tracking-widest opacity-60 mb-3">open a row to view a profile or terminate an account · xp proof is reviewed under xp verification</p>
      <Table testId="users-table" rows={rows} onRow={(u) => viewProfile(u.id, { noReview: true })} empty="no users match"
        cols={[
          { k: "name", label: "username", render: (u) => <span style={{ color: "var(--accent)" }}>{u.name || "—"}</span> },
          { k: "created_at", label: "joined", render: (u) => stamp(u.created_at), cls: "whitespace-nowrap" },
          { k: "provisions", label: "bag", render: (u) => (u.is_god ? "∞" : u.provisions) },
          { k: "asks", label: "asks" },
          { k: "answers", label: "answers" },
          { k: "completed", label: "undertaken" },
          { k: "rating", label: "rating", render: (u) => (u.rating ? `${Number(u.rating).toFixed(1)} · ${u.reviews}` : "—") },
          { k: "flags", label: "status", render: (u) => (
            <div className="flex flex-wrap gap-1">
              {u.is_god && <Chip on>member</Chip>}
              {u.is_genius && <Chip>remarkable</Chip>}
              {u.is_brave && <Chip>brave</Chip>}
              {u.xp_requested > 0 && <Chip on>{u.xp_requested} xp proof waiting</Chip>}
            </div>
          ) },
        ]} />
    </Panel>
  );
}

function FeatureForm({ ask, onDone }) {
  const [text, setText] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const n = Number(amount);
  const submit = async () => {
    if (busy || !(n >= 1)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/inquiries/${ask.id}/flag`, { category: "genius", appreciation: text, provisions: n });
      toast.success(`sent to remarkable minds${data.rewarded ? ` · +${data.rewarded} provisions` : ""}`);
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setBusy(false); }
  };
  return (
    <div className="mt-6 pt-5" style={{ borderTop: "1px solid var(--border)" }} data-testid="feature-form">
      <Label>send to remarkable minds</Label>
      <textarea data-testid="feature-appreciation" value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="why is this remarkable…" className="w-full p-3 text-sm resize-none" style={fieldStyle} />
      <div className="flex gap-3 mt-3">
        <input data-testid="feature-provisions" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ""))} placeholder="provisions" className="flex-1 px-3 py-2 text-sm" style={fieldStyle} />
        <button onClick={submit} disabled={busy || !(n >= 1)} data-testid="feature-submit" className="btn px-5 text-xs tracking-widest">{busy ? "…" : "feature"}</button>
      </div>
    </div>
  );
}

function AskDetail({ row, onClose, onChanged }) {
  const [d, reload] = useLoad(`/console/asks/${row.id}`);
  const del = async () => {
    if (!window.confirm("delete this ask and its answers?")) return;
    try { await api.delete(`/inquiries/${row.id}`); toast.success("ask deleted"); onChanged(); onClose(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delAnswer = async (a) => {
    if (!window.confirm("delete this answer? the ask returns to the pile.")) return;
    try { await api.delete(`/console/answers/${a.id}`); toast.success("answer deleted"); reload(); onChanged(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Modal title={`ask · ${row.subject}`} onClose={onClose} testId="ask-detail" wide>
      {!ready(d) ? <Gate data={d} testId="ask-detail" /> : (
        <>
          <div className="flex flex-wrap gap-3 items-center text-xs mb-3"><Chip on>{d.status}</Chip><span>by <span style={{ color: "var(--accent)" }}>{d.author}</span></span><span className="opacity-60">{stamp(d.created_at)}</span></div>
          <p className="long-text text-sm leading-relaxed" data-testid="ask-detail-text">{d.text}</p>
          <div className="mt-6"><Label>answers · {d.answers.length}</Label>
            {!d.answers.length ? <p className="text-xs opacity-60">no answers yet</p> : d.answers.map((a) => (
              <div key={a.id} className="p-3 mb-2" style={{ border: "1px solid var(--border)" }} data-testid={`ask-answer-${a.id}`}>
                <p className="long-text-sm text-sm leading-relaxed">{a.text}</p>
                <div className="flex items-center gap-3 mt-2 text-xs"><span style={{ color: "var(--accent)" }}>{a.author}</span><span className="opacity-60">{stamp(a.created_at)}</span>
                  <button onClick={() => delAnswer(a)} data-testid={`ask-answer-delete-${a.id}`} className="btn ml-auto px-3 py-1 text-[10px] tracking-widest">delete answer</button></div>
              </div>
            ))}
          </div>
          {row.flagged ? <p className="text-xs mt-6" style={{ color: "var(--accent)" }} data-testid="ask-featured">featured on remarkable minds</p> : <FeatureForm ask={row} onDone={() => { onChanged(); onClose(); }} />}
          <button onClick={del} data-testid="ask-delete" className="btn w-full py-3 mt-6 text-xs tracking-widest">delete ask</button>
        </>
      )}
    </Modal>
  );
}

export function Asks() {
  const [status, setStatus] = useState("");
  const [rows, reload] = useLoad(`/console/asks?status=${status}`, 15000);
  const [open, setOpen] = useState(null);
  return (
    <Panel title="asks & answers" testId="console-asks" right={<Filters testId="asks-filter" value={status} onChange={setStatus} options={[["", "all"], ["open", "open"], ["claimed", "held"], ["answered", "answered"]]} />}>
      <Table testId="asks-table" rows={rows} onRow={setOpen} empty="no asks"
        cols={[
          { k: "created_at", label: "when", render: (r) => stamp(r.created_at), cls: "whitespace-nowrap" },
          { k: "subject", label: "subject", render: (r) => <Chip>{r.subject}</Chip> },
          { k: "text", label: "ask", render: (r) => <span className="line-clamp-2">{r.text}</span> },
          { k: "author", label: "asker", render: (r) => <span style={{ color: "var(--accent)" }}>{r.author}</span> },
          { k: "status", label: "status", render: (r) => <div className="flex flex-col gap-1 items-start"><Chip on={r.status === "answered"}>{r.status === "claimed" ? `held · ${r.held_by || "—"}` : r.status}</Chip>{r.flagged && <Chip>remarkable</Chip>}</div> },
        ]} />
      {open && <AskDetail row={open} onClose={() => setOpen(null)} onChanged={reload} />}
    </Panel>
  );
}

export function Contemplations() {
  const [rows, reload] = useLoad("/console/posts");
  const [me] = useLoad("/auth/me");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const post = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try { await api.post("/posts", { text }); toast.success("contemplation posted"); setText(""); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async (p) => {
    if (!window.confirm("delete this contemplation?")) return;
    try { await api.delete(`/posts/${p.id}`); toast.success("deleted"); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <Panel className="lg:col-span-5" title="write a contemplation" testId="contemplation-compose">
        <textarea data-testid="contemplation-input" value={text} onChange={(e) => setText(limitWords(e.target.value, text, 1000))} rows={10} placeholder="shown on your member profile…" className="w-full p-3 text-sm resize-none" style={fieldStyle} />
        <div className="text-right text-[10px] opacity-60 mt-1">{countWords(text)} / 1,000 words</div>
        <button onClick={post} disabled={!text.trim() || busy} data-testid="contemplation-post" className="btn w-full py-3 mt-3 text-xs tracking-widest">{busy ? "…" : "post"}</button>
      </Panel>
      <Panel className="lg:col-span-7" title="all member contemplations" testId="contemplation-list">
        {!ready(rows) ? <Gate data={rows} testId="contemplations" /> : !rows.length ? <p className="text-xs opacity-60" data-testid="contemplation-empty">none posted yet</p> : rows.map((p) => (
          <article key={p.id} className="py-3" style={{ borderBottom: "1px dashed var(--border)" }} data-testid={`contemplation-${p.id}`}>
            <div className="flex items-center gap-3 text-xs mb-2"><Chip on>{p.author}</Chip><span className="opacity-60">{stamp(p.created_at)}</span>
              {me?.id === p.user_id && <button onClick={() => del(p)} data-testid={`contemplation-delete-${p.id}`} className="btn ml-auto px-3 py-1 text-[10px] tracking-widest">delete</button>}</div>
            <p className="long-text-sm text-sm leading-relaxed whitespace-pre-wrap">{p.text}</p>
          </article>
        ))}
      </Panel>
    </div>
  );
}
